import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useMidApplications } from "hooks/crm/useMidApplications";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import {
  MID_APP_STATUS,
  statusToneFrom,
  openPendCount,
} from "utilities/statusTones";
import RecordTable from "components/Records/RecordTable";
import RecordStatusPill from "components/Records/RecordStatusPill";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import RelativeTime from "components/RelativeTime";
import ContainerScopeNotice from "components/ContainerScopeNotice";
import SourceBadge from "components/SourceBadge";
import { FileText } from "lucide-react";

const appTone = statusToneFrom(MID_APP_STATUS);
const FIELDS = filterFieldsFor("companyMidApplications");

// SOUND-689: imported, not redefined. This was a THIRD copy of "what is an open
// pend" (with a comment claiming to be the only one), and it still carried the
// embedded-array fallback after that array was dropped from all 1,008 documents.

function MidApplicationsTabInner({ companyId, isPartnerView }) {
  const navigate = useNavigate();
  const { filters, set } = useSearch();
  const { user } = useAuth();
  const internal = isInternal(user);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-updatedAt");

  // companyId is FORCED by the record, not chosen — see companyMidApplications
  // in searchConfig. Replaces a fetch capped at limit=100 whose search and
  // status filter then ran CLIENT-SIDE over those 100 rows, so a company with
  // more applications than that filtered a truncated set without saying so.
  const { applications, pagination, loading, error, refetch } = useMidApplications({
    filters: { ...filters, companyId },
    page,
    limit,
    sort,
  });

  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, FIELDS);

  // Aggregated = a container company (partner/parent) whose rows belong to
  // related companies. Derived from the data, so the Client column appears only
  // where it distinguishes anything.
  const aggregated = applications.some(
    (a) => a.companyId && a.companyId !== companyId
  );

  const columns = useMemo(
    () => [
      aggregated && {
        key: "client",
        header: "Client",
        width: "20%",
        exportValue: (app) => app.clientName || app.companyName || "",
        cell: (app) => (
          <div className="truncate font-medium">
            {app.clientName || app.companyName || "—"}
          </div>
        ),
      },
      {
        key: "dbaName",
        header: "DBA",
        sortKey: "dbaName",
        width: aggregated ? "20%" : "26%",
        cell: (app) => app.dbaName || "—",
      },
      {
        key: "pends",
        header: "Pends",
        width: "13%",
        sortKey: "unresolvedPendCount",
        defaultSortDir: "desc",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        exportValue: openPendCount,
        cell: (app) => {
          const unresolved = openPendCount(app);
          return unresolved > 0 ? (
            <span className="badge badge-warning">{unresolved} unresolved</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        key: "processorName",
        header: "Processor",
        width: aggregated ? "16%" : "20%",
        sortKey: "processorName",
        cell: (app) => app.processorName || "—",
      },
      internal && {
        key: "source",
        header: "Source",
        width: "13%",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        exportValue: (app) => app.source?.type || "",
        cell: (app) => <SourceBadge source={app.source} />,
      },
      {
        key: "status",
        header: "Status",
        width: "13%",
        sortKey: "currentStatus",
        exportValue: (app) =>
          MID_APP_STATUS[app.currentStatus]?.label || app.currentStatus || "",
        cell: (app) => <RecordStatusPill status={app.currentStatus} toneFor={appTone} />,
      },
      {
        key: "updatedAt",
        header: "Updated",
        width: "12%",
        sortKey: "updatedAt",
        defaultSortDir: "desc",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell text-muted-foreground",
        exportValue: (app) => app.updatedAt || "",
        cell: (app) => <RelativeTime value={app.updatedAt} />,
      },
    ],
    [aggregated, internal]
  );

  return (
    <div className="space-y-4">
      {(isPartnerView || aggregated) && (
        <ContainerScopeNotice
          variant={isPartnerView ? "partner" : "parent"}
          noun="applications"
        />
      )}

      <ListSearchBar
        value={filters.search || ""}
        onChange={(v) => set("search", v)}
        placeholder="Search DBA or processor"
      >
        <select
          className="select w-auto"
          value={filters.status || ""}
          onChange={(e) => set("status", e.target.value)}
          aria-label="Status"
        >
          <option value="">Status: Any</option>
          {Object.entries(MID_APP_STATUS).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </ListSearchBar>

      <RecordTable
        columns={columns}
        rows={applications}
        rowKey={(app) => app.applicationId}
        onRowClick={(app) => navigate(`/mid-applications/${app.applicationId}`)}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={activeFilterCount > 0}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
        noMatch={{ icon: FileText, heading: "No matching applications" }}
        empty={{
          icon: FileText,
          heading: isPartnerView
            ? "No applications across this partner's attributed clients yet"
            : "No applications found for this company",
          errorText: "Failed to load applications.",
        }}
      />

      <ListPagination
        page={pagination.page || page}
        totalPages={pagination.totalPages || 0}
        total={pagination.total || 0}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />
    </div>
  );
}

export default function MidApplicationsTab({ companyId, isPartnerView = false }) {
  return (
    <SearchProvider namespace="companyMidApplications">
      <MidApplicationsTabInner companyId={companyId} isPartnerView={isPartnerView} />
    </SearchProvider>
  );
}
